import { useEffect, useMemo, useRef, useState } from 'react';
import {
  amountDueChargeIds,
  getNetDueForEachBatched,
  invoiceAmountDueChargeIds,
  type AmountDueArgroup,
  type AmountDueChargeRow,
} from '@hopdrive/sdk-ar';
import { getAmountDueExecutor } from '../services/amountDueExecutor';

/**
 * An invoice's Amount Due as a screen holds it. It is sdk-ar's answer or
 * nothing: while it loads, and when it cannot be read, the screen says so and
 * never fills in a figure of its own.
 */
export type InvoiceAmountDue = { status: 'loading' } | { status: 'ready'; amount: number } | { status: 'unavailable' };

/** What a screen prints in place of the figure when sdk-ar could not answer. */
export const AMOUNT_DUE_UNAVAILABLE_TEXT = 'Unavailable';

const LOADING: InvoiceAmountDue = { status: 'loading' };

/**
 * sdk-ar's net due on each set of charge ids, in order. Every ask made before
 * the next task shares one load, so an invoice's total and each rooftop's
 * table read the invoice once between them; when that load fails, each ask is
 * retried on its own so only the failing table says so. The reads skip
 * Apollo's cache (see amountDueExecutor).
 */
function askNetDues(sets: number[][]): Promise<number[]> {
  return getNetDueForEachBatched(sets, { executor: getAmountDueExecutor() });
}

/**
 * sdk-ar's Amount Due for the invoice whose billing groups are `argroups`.
 * It asks again when what the answer depends on changes (its charges, their
 * settled payments and its credits), not when `argroups` is merely a new array: the
 * invoice's charges query skips Apollo's cache, so every refetch hands over new
 * objects with the same values. A payment, a credit or a charge change asks
 * again. Null asks nothing and stays loading.
 */
export function useInvoiceAmountDue(argroups: ReadonlyArray<AmountDueArgroup> | null | undefined): InvoiceAmountDue {
  const signature = useMemo(() => (argroups ? argroupsSignature(argroups) : null), [argroups]);
  // The groups the signature was taken from, for the ask below.
  const latestArgroups = useRef(argroups);
  useEffect(() => {
    latestArgroups.current = argroups;
  });

  // The answer remembers which signature it answered, so a changed one reads
  // as loading until its own answer arrives.
  const [answer, setAnswer] = useState<{ signature: string; amountDue: InvoiceAmountDue } | null>(null);

  useEffect(() => {
    const current = latestArgroups.current;
    if (signature === null || !current) return undefined;
    let cancelled = false;
    askNetDues([invoiceAmountDueChargeIds(current)]).then(([amount]) => amount).then(
      (amount) => {
        if (!cancelled) setAnswer({ signature, amountDue: { status: 'ready', amount } });
      },
      () => {
        if (!cancelled) setAnswer({ signature, amountDue: { status: 'unavailable' } });
      }
    );
    return () => {
      cancelled = true;
    };
  }, [signature]);

  return answer && signature !== null && answer.signature === signature ? answer.amountDue : LOADING;
}

/**
 * What an invoice's answer depends on. The screen hands over its narrow amount
 * due groups (charge ids and amounts, settled payment sums, credit sums), so
 * all of their values count and the text stays small.
 */
function argroupsSignature(argroups: ReadonlyArray<AmountDueArgroup>): string {
  return JSON.stringify(argroups, (_key, value) => (typeof value === 'bigint' ? String(value) : value));
}

/** A move row as the batched hook reads it: the charges it asks about, and the credits on them. */
export interface AmountDueRow {
  charges: ReadonlyArray<AmountDueChargeRow & { amount?: unknown; arpaymentdetails?: ReadonlyArray<AllocationSignatureRow> | null }>;
  credits?: ReadonlyArray<{ id?: unknown; amount?: unknown }>;
}

interface AllocationSignatureRow {
  id?: unknown;
  amount?: unknown;
  parent?: { status?: unknown } | null;
}

/**
 * What the rows' answers depend on, as text: each charge with its amount and
 * payment allocations, and each credit. The table rebuilds its rows, and
 * copies its charges, on every render, so this compares values instead of
 * objects. It asks again after a payment or a credit and never on a plain
 * re-render.
 */
function rowsSignature(rows: ReadonlyArray<AmountDueRow>): string {
  return rows
    .map((row) =>
      [
        row.charges
          .map((charge) =>
            [
              String(charge.id),
              String(charge.amount),
              String(charge.active),
              String(charge.type),
              (charge.arpaymentdetails ?? [])
                .map((detail) => `${String(detail.id)}:${String(detail.amount)}:${String(detail.parent?.status)}`)
                .join(','),
            ].join('/')
          )
          .join(';'),
        (row.credits ?? []).map((credit) => `${String(credit.id)}:${String(credit.amount)}`).join(';'),
      ].join('|')
    )
    .join('\n');
}

/**
 * sdk-ar's Amount Due for each move row of an invoice, from one read, in the
 * order of `rows`. Each is sdk-ar's `getChargesAmountDue` for that row alone.
 * Null asks nothing and every row stays loading.
 */
export function useMoveAmountDues(rows: ReadonlyArray<AmountDueRow> | null | undefined): InvoiceAmountDue[] {
  // Strings, so a re-render with equal rows asks nothing, and a payment or a
  // credit that changes a row asks again.
  const signature = rows ? rowsSignature(rows) : null;
  const idSets = rows ? JSON.stringify(rows.map((row) => amountDueChargeIds(row.charges))) : null;
  const [answer, setAnswer] = useState<{ signature: string; amounts: InvoiceAmountDue[] } | null>(null);

  useEffect(() => {
    if (signature === null || idSets === null) return undefined;
    const sets: number[][] = JSON.parse(idSets);
    let cancelled = false;
    askNetDues(sets).then(
      (amounts) => {
        if (!cancelled) setAnswer({ signature, amounts: amounts.map((amount): InvoiceAmountDue => ({ status: 'ready', amount })) });
      },
      () => {
        if (!cancelled) setAnswer({ signature, amounts: sets.map((): InvoiceAmountDue => ({ status: 'unavailable' })) });
      }
    );
    return () => {
      cancelled = true;
    };
  }, [signature, idSets]);

  if (answer && answer.signature === signature) return answer.amounts;
  return (rows ?? []).map(() => LOADING);
}
